// src/components/layout/ContactModal.js
// ✅ 수정사항:
//   1. Firestore 보안 규칙 호환 - status/source 필드 포함 create 허용
//   2. Honeypot 스팸 방지 (숨겨진 website 필드 - 봇만 채움)
//   3. Rate limiting - 동일 이메일 30초 내 중복 전송 방지
//   4. 이메일 형식 검증 강화
//   5. 에러 메시지 상세화

import React, { useState, useRef } from 'react';
import { Mail, Phone, User, MessageSquare, Send, X } from 'lucide-react';
import { db } from '../../firebase/config';
import { collection, addDoc, serverTimestamp, query, where, getDocs, Timestamp } from 'firebase/firestore';

// ─── 유틸 ────────────────────────────────────────────────────────────────────

const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// ─── 컴포넌트 ─────────────────────────────────────────────────────────────────

const ContactModal = ({ onClose }) => {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    website: '', // ← Honeypot: 사용자에게 안 보임, 봇만 채움
  });
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const lastSubmitRef = useRef({}); // { [email]: timestamp } — Rate limit 추적

  const handleInputChange = (e) => {
    setContactForm({ ...contactForm, [e.target.name]: e.target.value });
  };

  // ─── 제출 핸들러 ─────────────────────────────────────────────────────────

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // 1. Honeypot 체크 — 채워져 있으면 봇으로 판단, 조용히 무시
      if (contactForm.website.trim() !== '') {
        console.warn('스팸 봇 감지됨 (honeypot)');
        setShowSuccessPopup(true); // 봇에게는 성공처럼 보여줌
        return;
      }

      // 2. 필수값 검증
      if (!contactForm.name.trim()) throw new Error('이름을 입력해주세요.');
      if (!contactForm.email.trim()) throw new Error('이메일을 입력해주세요.');
      if (!isValidEmail(contactForm.email.trim()))
        throw new Error('올바른 이메일 주소를 입력해주세요.');
      if (!contactForm.message.trim()) throw new Error('문의 내용을 입력해주세요.');
      if (contactForm.message.trim().length < 10)
        throw new Error('문의 내용을 10자 이상 입력해주세요.');

      // 3. Rate limit — 같은 이메일로 30초 이내 재전송 차단 (클라이언트)
      const emailKey = contactForm.email.trim().toLowerCase();
      const now = Date.now();
      if (lastSubmitRef.current[emailKey] && now - lastSubmitRef.current[emailKey] < 30000) {
        throw new Error('잠시 후 다시 시도해주세요. (30초 대기)');
      }

      // 4. Firestore 저장
      //    ⚠️ 필드명을 Firestore 보안 규칙의 hasAll([...])과 정확히 일치시켜야 함
      const contactData = {
        name:    contactForm.name.trim(),
        email:   emailKey,
        phone:   contactForm.phone?.trim() || null,
        message: contactForm.message.trim(),
        status:  'unread',
        source:  'homepage',
        createdAt: serverTimestamp(),
      };

      const docRef = await addDoc(collection(db, 'contacts'), contactData);
      console.log('문의 저장 성공. ID:', docRef.id);

      // Rate limit 타임스탬프 기록
      lastSubmitRef.current[emailKey] = now;

      setShowSuccessPopup(true);

    } catch (error) {
      console.error('문의 저장 오류:', error);

      // 에러 종류별 사용자 메시지
      const errorMap = {
        'permission-denied': '전송 권한이 없습니다. 잠시 후 다시 시도해주세요.',
        'unavailable':       '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.',
        'resource-exhausted': '일시적으로 요청이 많습니다. 잠시 후 다시 시도해주세요.',
        'deadline-exceeded':  '요청 시간이 초과되었습니다. 다시 시도해주세요.',
      };

      const message =
        errorMap[error.code] ||
        (error.message.length < 100 ? error.message : '전송 중 오류가 발생했습니다. 다시 시도해주세요.');

      alert(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── 성공 후 처리 ─────────────────────────────────────────────────────────

  const resetAndClose = () => {
    setShowSuccessPopup(false);
    setContactForm({ name: '', email: '', phone: '', message: '', website: '' });
    onClose();
  };

  // ─── 스타일 ───────────────────────────────────────────────────────────────

  const styles = {
    backdrop: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000,
    },
    modal: {
      backgroundColor: 'white', borderRadius: '0.5rem', padding: '2rem',
      maxWidth: '28rem', width: '100%', margin: '1rem', position: 'relative',
      boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)',
    },
    closeButton: {
      position: 'absolute', top: '1rem', right: '1rem',
      background: 'none', border: 'none', color: '#9ca3af',
      cursor: 'pointer', padding: '0.25rem', borderRadius: '0.25rem',
    },
    title: { fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem', color: '#1f2937' },
    form: { display: 'flex', flexDirection: 'column', gap: '1rem' },
    inputGroup: { display: 'flex', flexDirection: 'column' },
    label: { fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' },
    inputContainer: { position: 'relative' },
    input: {
      width: '100%', paddingLeft: '2.5rem', paddingRight: '0.75rem',
      paddingTop: '0.5rem', paddingBottom: '0.5rem',
      border: '1px solid #d1d5db', borderRadius: '0.5rem',
      fontSize: '1rem', fontFamily: 'inherit', outline: 'none',
      boxSizing: 'border-box', opacity: isSubmitting ? 0.6 : 1,
    },
    textarea: {
      width: '100%', paddingLeft: '2.5rem', paddingRight: '0.75rem',
      paddingTop: '0.5rem', paddingBottom: '0.5rem',
      border: '1px solid #d1d5db', borderRadius: '0.5rem',
      fontSize: '1rem', fontFamily: 'inherit', outline: 'none',
      resize: 'none', minHeight: '100px', boxSizing: 'border-box',
      opacity: isSubmitting ? 0.6 : 1,
    },
    icon: { position: 'absolute', left: '0.75rem', top: '0.75rem', color: '#9ca3af' },
    buttonContainer: { display: 'flex', gap: '1rem', marginTop: '1rem' },
    submitButton: {
      flex: 1, backgroundColor: isSubmitting ? '#9ca3af' : '#16a34a',
      color: 'white', padding: '0.75rem', border: 'none', borderRadius: '0.5rem',
      fontSize: '1rem', cursor: isSubmitting ? 'not-allowed' : 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: '0.5rem', fontFamily: 'inherit',
    },
    cancelButton: {
      flex: 1, backgroundColor: '#e5e7eb', color: '#374151',
      padding: '0.75rem', border: 'none', borderRadius: '0.5rem',
      fontSize: '1rem', cursor: isSubmitting ? 'not-allowed' : 'pointer',
      fontFamily: 'inherit', opacity: isSubmitting ? 0.6 : 1,
    },
    // 성공 팝업
    successBackdrop: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1100, backdropFilter: 'blur(4px)',
    },
    successModal: {
      backgroundColor: 'white', maxWidth: '600px', width: '90%',
      maxHeight: '80vh', borderRadius: '16px',
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)', overflow: 'hidden',
    },
    successHeader: {
      background: 'linear-gradient(135deg, #22c55e, #16a34a)',
      color: 'white', padding: '24px', textAlign: 'center', position: 'relative',
    },
    successTitle: { fontSize: '1.5rem', fontWeight: '600', marginBottom: '4px' },
    successSubtitle: { opacity: 0.9, fontSize: '0.95rem' },
    successCloseBtn: {
      position: 'absolute', top: '16px', right: '20px',
      background: 'none', border: 'none', color: 'white',
      fontSize: '24px', cursor: 'pointer',
    },
    successBody: { padding: '32px 24px', maxHeight: '60vh', overflowY: 'auto' },
    successIconCircle: {
      width: '80px', height: '80px', background: '#22c55e', borderRadius: '50%',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '40px', color: 'white', marginBottom: '16px',
    },
    infoSection: { marginBottom: '24px' },
    infoSectionTitle: {
      color: '#22c55e', fontSize: '1.1rem', fontWeight: '600', marginBottom: '12px',
    },
    infoItem: { marginBottom: '8px', paddingLeft: '16px', color: '#4b5563' },
    messageContent: {
      background: '#f9fafb', padding: '16px', borderRadius: '8px',
      borderLeft: '4px solid #22c55e', marginTop: '8px',
      color: '#374151', lineHeight: '1.5',
    },
    footerInfo: {
      background: '#f8f9fa', padding: '16px', borderRadius: '8px',
      marginTop: '20px', fontSize: '0.9rem', color: '#6b7280',
    },
    actionButtons: {
      display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '32px',
    },
    btnPrimary: {
      padding: '12px 24px', borderRadius: '8px', fontWeight: '500',
      cursor: 'pointer', fontSize: '0.95rem', border: 'none',
      background: '#22c55e', color: 'white', fontFamily: 'inherit',
    },
    btnSecondary: {
      padding: '12px 24px', borderRadius: '8px', fontWeight: '500',
      cursor: 'pointer', fontSize: '0.95rem', border: '1px solid #d1d5db',
      background: '#f3f4f6', color: '#374151', fontFamily: 'inherit',
    },
  };

  const focusStyle = (e) => {
    if (!isSubmitting) {
      e.target.style.borderColor = '#16a34a';
      e.target.style.boxShadow = '0 0 0 3px rgba(22, 163, 74, 0.1)';
    }
  };
  const blurStyle = (e) => {
    e.target.style.borderColor = '#d1d5db';
    e.target.style.boxShadow = 'none';
  };

  // ─── 렌더 ─────────────────────────────────────────────────────────────────

  return (
    <>
      {/* ── Contact 모달 ── */}
      <div style={styles.backdrop} onClick={onClose}>
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          <button style={styles.closeButton} onClick={onClose} disabled={isSubmitting}>
            <X size={24} />
          </button>

          <h2 style={styles.title}>Contact Us</h2>

          <form style={styles.form} onSubmit={handleFormSubmit}>

            {/* ✅ Honeypot 필드 — 완전히 숨김, 봇만 채울 수 있음 */}
            <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', opacity: 0, pointerEvents: 'none' }}
                 aria-hidden="true">
              <input
                type="text"
                name="website"
                value={contactForm.website}
                onChange={handleInputChange}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            {/* 이름 */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>이름 *</label>
              <div style={styles.inputContainer}>
                <User style={styles.icon} size={16} />
                <input
                  type="text" name="name" value={contactForm.name}
                  onChange={handleInputChange} required disabled={isSubmitting}
                  style={styles.input} placeholder="이름을 입력하세요"
                  maxLength="100" onFocus={focusStyle} onBlur={blurStyle}
                />
              </div>
            </div>

            {/* 이메일 */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>이메일 *</label>
              <div style={styles.inputContainer}>
                <Mail style={styles.icon} size={16} />
                <input
                  type="email" name="email" value={contactForm.email}
                  onChange={handleInputChange} required disabled={isSubmitting}
                  style={styles.input} placeholder="이메일을 입력하세요"
                  maxLength="200" onFocus={focusStyle} onBlur={blurStyle}
                />
              </div>
            </div>

            {/* 연락처 */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>연락처</label>
              <div style={styles.inputContainer}>
                <Phone style={styles.icon} size={16} />
                <input
                  type="tel" name="phone" value={contactForm.phone}
                  onChange={handleInputChange} disabled={isSubmitting}
                  style={styles.input} placeholder="연락처를 입력하세요 (선택사항)"
                  maxLength="20" onFocus={focusStyle} onBlur={blurStyle}
                />
              </div>
            </div>

            {/* 문의 내용 */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>문의 내용 *</label>
              <div style={styles.inputContainer}>
                <MessageSquare style={styles.icon} size={16} />
                <textarea
                  name="message" value={contactForm.message}
                  onChange={handleInputChange} required disabled={isSubmitting}
                  style={styles.textarea}
                  placeholder="문의사항이나 제안하고 싶은 아이디어를 자세히 입력해주세요"
                  maxLength="2000" onFocus={focusStyle} onBlur={blurStyle}
                />
              </div>
            </div>

            {/* 버튼 */}
            <div style={styles.buttonContainer}>
              <button
                type="submit" disabled={isSubmitting} style={styles.submitButton}
                onMouseEnter={(e) => { if (!isSubmitting) e.currentTarget.style.backgroundColor = '#15803d'; }}
                onMouseLeave={(e) => { if (!isSubmitting) e.currentTarget.style.backgroundColor = '#16a34a'; }}
              >
                <Send size={16} />
                {isSubmitting ? '전송 중...' : 'Send'}
              </button>
              <button
                type="button" onClick={onClose} disabled={isSubmitting}
                style={styles.cancelButton}
                onMouseEnter={(e) => { if (!isSubmitting) e.currentTarget.style.backgroundColor = '#d1d5db'; }}
                onMouseLeave={(e) => { if (!isSubmitting) e.currentTarget.style.backgroundColor = '#e5e7eb'; }}
              >
                취소
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* ── 성공 팝업 ── */}
      {showSuccessPopup && (
        <div style={styles.successBackdrop} onClick={resetAndClose}>
          <div style={styles.successModal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.successHeader}>
              <button style={styles.successCloseBtn} onClick={resetAndClose}>×</button>
              <h2 style={styles.successTitle}>[Abocado 문의] {contactForm.name}님의 문의사항</h2>
              <p style={styles.successSubtitle}>문의가 성공적으로 접수되었습니다</p>
            </div>

            <div style={styles.successBody}>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <div style={styles.successIconCircle}>✓</div>
              </div>

              <p style={{ textAlign: 'center', marginBottom: '24px', color: '#1f2937', fontSize: '1.1rem' }}>
                안녕하세요, <strong>Abocado</strong>입니다.<br />
                새로운 문의사항이 접수되었습니다.
              </p>

              <div style={styles.infoSection}>
                <h3 style={styles.infoSectionTitle}>📝 문의자 정보:</h3>
                <div style={styles.infoItem}><strong>이름:</strong> {contactForm.name}</div>
                <div style={styles.infoItem}><strong>이메일:</strong> {contactForm.email}</div>
                <div style={styles.infoItem}><strong>연락처:</strong> {contactForm.phone || '미제공'}</div>
              </div>

              <div style={styles.infoSection}>
                <h3 style={styles.infoSectionTitle}>💬 문의 내용:</h3>
                <div style={styles.messageContent}>{contactForm.message}</div>
              </div>

              <div style={styles.footerInfo}>
                <div><strong>접수일시:</strong> {new Date().toLocaleString('ko-KR')}</div>
                <div><strong>문의 경로:</strong> Abocado 홈페이지</div>
                <div style={{ marginTop: '12px', color: '#22c55e', fontWeight: '500' }}>
                  빠른 시일 내에 연락드리겠습니다.
                </div>
                <div style={{ textAlign: 'right', marginTop: '8px', fontWeight: '500' }}>
                  감사합니다. - <strong>Abocado 팀</strong>
                </div>
              </div>

              <div style={styles.actionButtons}>
                <button style={styles.btnPrimary} onClick={resetAndClose}>확인</button>
                <button style={styles.btnSecondary} onClick={resetAndClose}>홈페이지로 돌아가기</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ContactModal;
